import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

export interface ProductCartItem {
  id: string;
  name: string;
  rate: number;
  quantity: number;
}

// Extra line items (e.g. add-ons), merged in from the second slice
export interface CartExtra {
  id?: string;
  name?: string;
  qty?: number;
  price: number;
}

interface ServiceFeeState {
  items: ProductCartItem[];
  extras: CartExtra[];
  total: number;              // subtotal before delivery
  extrasTotal: number;
  guestsFee: number;
  hostsFee: number;
  hostProfit: number;
  platformProfit: number;
  totalCharged: number;
  discountPercent: number;
  discountedTotal: number;
  earningsUpdated: boolean;
  deliveryFee: number;
  loading: boolean;
  error: string | null;
}

// ─────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────

// Host currently gets 100% (you can adjust)
export const calculateHostEarnings = (total: number) => {
  const hostFeePercentage = 0;
  const hostsFee = total * hostFeePercentage;
  const hostProfit = parseFloat((total - hostsFee).toFixed(2));
  return { hostProfit, hostsFee };
};

// Same guest fee logic as furniture
export const calculateGuestServiceFee = (subtotal: number): number => {
  let guestsFee: number;

  if (subtotal > 5000) guestsFee = subtotal * 0.2;
  else if (subtotal > 1000) guestsFee = subtotal * 0.2;
  else if (subtotal > 500) guestsFee = subtotal * 0.12;
  else if (subtotal > 100) guestsFee = subtotal * 0.1;
  else guestsFee = subtotal * 0.08;

  return parseFloat(guestsFee.toFixed(2));
};

export const calculatePlatformProfit = (
  hostsFee: number,
  guestsFee: number
): number => parseFloat((hostsFee + guestsFee).toFixed(2));

// Extras total helper, merged in from the second slice
export const calculateExtrasTotal = (extras: CartExtra[]): number => {
  const extrasTotal = extras.reduce((sum: number, extra: CartExtra) => {
    return sum + (extra.qty || 1) * extra.price;
  }, 0);
  return parseFloat(extrasTotal.toFixed(2));
};

// ─────────────────────────────────────────────
// MAIN CALCULATION THUNK
// ─────────────────────────────────────────────

export const calculateEarnings = createAsyncThunk(
  "serviceFee/calculateProductsEarnings",
  async ({
    items,
    extras = [],
    total,
    deliveryFee = 0,
    discountApplied = {},
  }: {
    items: ProductCartItem[];
    extras?: CartExtra[];
    total: number;
    deliveryFee?: number;
    discountApplied?: {
      is5?: boolean;
      is10?: boolean;
      is25?: boolean;
      is50?: boolean;
      is80?: boolean;
    };
  }) => {
    // 1️⃣ Extras total
    const extrasTotal = calculateExtrasTotal(extras);

    // 2️⃣ Add extras + delivery to total
    const totalWithDelivery = total + extrasTotal + deliveryFee;

    // 3️⃣ Discount logic
    let discountPercent = 0;
    if (discountApplied.is80) discountPercent = 0.8;
    else if (discountApplied.is50) discountPercent = 0.5;
    else if (discountApplied.is25) discountPercent = 0.25;
    else if (discountApplied.is10) discountPercent = 0.1;
    else if (discountApplied.is5) discountPercent = 0.05;

    // 4️⃣ Apply discount
    const discountedTotal = totalWithDelivery * (1 - discountPercent);

    // 5️⃣ Guest service fee (applied on discounted total)
    const guestsFee = calculateGuestServiceFee(discountedTotal);

    // 6️⃣ Host & platform earnings
    const { hostProfit, hostsFee } = calculateHostEarnings(discountedTotal);
    const platformProfit = calculatePlatformProfit(hostsFee, guestsFee);

    // 7️⃣ Total charge to guest
    const totalCharged = parseFloat(
      (discountedTotal + guestsFee).toFixed(2)
    );

    return {
      items,
      extras,
      extrasTotal,
      total,              // original price of items (no delivery/extras)
      deliveryFee,
      discountPercent,
      discountedTotal,
      guestsFee,
      hostsFee,
      hostProfit,
      platformProfit,
      totalCharged,
    };
  }
);

// ─────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────

const initialState: ServiceFeeState = {
  items: [],
  extras: [],
  total: 0,
  extrasTotal: 0,
  guestsFee: 0,
  hostsFee: 0,
  hostProfit: 0,
  platformProfit: 0,
  totalCharged: 0,
  discountPercent: 0,
  discountedTotal: 0,
  earningsUpdated: false,
  deliveryFee: 0,
  loading: false,
  error: null,
};

const serviceFeeSlice = createSlice({
  name: "serviceFee",
  initialState,
  reducers: {
    resetEarnings: (state) => {
      state.items = [];
      state.extras = [];
      state.total = 0;
      state.extrasTotal = 0;
      state.guestsFee = 0;
      state.hostsFee = 0;
      state.hostProfit = 0;
      state.platformProfit = 0;
      state.totalCharged = 0;
      state.discountPercent = 0;
      state.discountedTotal = 0;
      state.earningsUpdated = false;
      state.deliveryFee = 0;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(calculateEarnings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(calculateEarnings.fulfilled, (state, action) => {
        const {
          items,
          extras,
          extrasTotal,
          total,
          deliveryFee,
          discountPercent,
          discountedTotal,
          guestsFee,
          hostsFee,
          hostProfit,
          platformProfit,
          totalCharged,
        } = action.payload;

        state.items = items;
        state.extras = extras;
        state.extrasTotal = extrasTotal;
        state.total = total;
        state.deliveryFee = deliveryFee;
        state.discountPercent = discountPercent;
        state.discountedTotal = discountedTotal;
        state.guestsFee = guestsFee;
        state.hostsFee = hostsFee;
        state.hostProfit = hostProfit;
        state.platformProfit = platformProfit;
        state.totalCharged = totalCharged;
        state.earningsUpdated = true;
        state.loading = false;
      })
      .addCase(calculateEarnings.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.error.message as string) || "Failed to calculate earnings";
      });
  },
});

export const { resetEarnings } = serviceFeeSlice.actions;
export default serviceFeeSlice.reducer;